import moment from "moment/moment";
import Settings from "@/config.json";
import StackTrace from 'stacktrace-js';

export default {
    generateHelpers: function () {
        return {
            GetCookie(cname) {
                var name = cname + "=";
                var decodedCookie = decodeURIComponent(document.cookie);
                var ca = decodedCookie.split(';');
                for (var i = 0; i < ca.length; i++) {
                    var c = ca[i];
                    while (c.charAt(0) == ' ') {
                        c = c.substring(1);
                    }
                    if (c.indexOf(name) == 0) {
                        return c.substring(name.length, c.length);
                    }
                }
                return "";
            },
            SetCookie(cname, cvalue, exdays) {
                var d = new Date();
                d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
                var expires = "expires=" + d.toUTCString();
                document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
            },
            CheckEmail(email) {
                var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return re.test(email);
            },
            CheckOIB(oib) {
                oib = oib.toString();
                if (oib.length !== 11) return false;

                var b = parseInt(oib, 10);
                if (isNaN(b)) return false;

                var a = 10;
                for (var i = 0; i < 10; i++) {
                    a = a + parseInt(oib.substr(i, 1), 10);
                    a = a % 10;
                    if (a === 0) a = 10;
                    a *= 2;
                    a = a % 11;
                }
                var control = 11 - a;
                if (control === 10) control = 0;

                return control === parseInt(oib.substr(10, 1));
            },
            // Returns true if the logged in user holds at least one of the given permissions.
            // Used to gate menu items, routes and UI elements. The equivalent checks are
            // enforced server-side through the permission middleware.
            HasAnyPermission(vm, permissions) {
                if (vm.$store.state.user === null) return false;
                if (!Array.isArray(vm.$store.state.user.permissions)) return false;
                for (let permission of permissions) {
                    if (vm.$store.state.user.permissions.includes(permission)) {
                        return true;
                    }
                }
                return false;
            },
            GetYesNoAllOptions(vm) {
                return [
                    {
                        label: vm.$LANG.Get('options.yes'),
                        value: 1
                    },
                    {
                        label: vm.$LANG.Get('options.no'),
                        value: 0
                    },
                    {
                        label: vm.$LANG.Get('options.all'),
                        value: -1
                    }
                ]
            },
            // Weekdays keyed by ISO day of week (1 = Monday ... 7 = Sunday). Shared by every component
            // that needs to list days of the week (e.g. EntityMembership schedule, EntitySession schedule).
            // Pass includeHoliday = true to append the synthetic "Holiday" row (value 8) used by the working
            // hours schedules; it is left out by default so day-of-week pickers (e.g. recurring sessions)
            // don't offer it.
            GetWeekdays(vm, includeHoliday = false) {
                let weekdays = [];
                for (let day = 1; day <= 7; day++) {
                    weekdays.push({
                        value: day,
                        label: vm.$LANG.Get('labels.weekdays.' + day)
                    });
                }
                if (includeHoliday) {
                    weekdays.push({
                        value: 8,
                        label: vm.$LANG.Get('labels.weekdays.8')
                    });
                }
                return weekdays;
            },
            DatePattern() {
                return /(?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)/;
            },
            DateTimePattern() {
                return /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])) (?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)/
            },
            FormatMoney(amount, decimalCount = 2, decimal = ",", thousands = ".") {
                decimalCount = Math.abs(decimalCount);
                decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

                const negativeSign = amount < 0 ? "-" : "";

                let i = parseInt(amount = Math.abs(Number(amount) || 0).toFixed(decimalCount)).toString();
                let j = (i.length > 3) ? i.length % 3 : 0;

                return negativeSign + (j ? i.substr(0, j) + thousands : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) + (decimalCount ? decimal + Math.abs(amount - i).toFixed(decimalCount).slice(2) : "");
            },
            FormatDate(date) {
                let dateArray = date.split('-');
                return dateArray[2] + '.' + dateArray[1] + '.' + dateArray[0] + '.';
            },
            //long customer names would push the other columns out of a grid, so only the beginning is shown
            ShortCustomer(customer) {
                if (customer.length <= 25) return customer;
                return customer.substring(0, 25) + '...';
            },
            ToCroatianLower(s) {
                let swaps = {
                    'c': ['č', 'Č', 'ć', 'Ć'],
                    'd': ['đ', 'Đ'],
                    's': ['š', 'Š'],
                    'z': ['ž', 'Ž']
                }
                for (let to in swaps) {
                    for (let from of swaps[to]) {
                        s = s.split(from).join(to);
                    }
                }
                s = s.toLowerCase();
                return s;
            },
            /**
             * The first day from today on that none of the given person memberships ({start_date, end_date} as
             * YYYY-MM-DD, e.g. person-memberships/by-person records) covers: today when nothing is running,
             * otherwise the day after the running ones end - and past any membership that picks up right after
             * those (a renewal already bought), so a new membership never overlaps what is already paid for.
             * YYYY-MM-DD strings order correctly with plain comparison.
             */
            FirstFreeMembershipDate(records) {
                let date = moment().format('YYYY-MM-DD');
                let moved = true;
                while (moved) {
                    moved = false;
                    for (let record of records) {
                        if (record.start_date <= date && record.end_date >= date) {
                            date = moment(record.end_date, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
                            moved = true;
                        }
                    }
                }
                return date;
            },
            GetDefaultCurrency() {
                if (moment().format('YYYY-MM-DD HH:mm:ss') < Settings.EUR_TRANSITION) {
                    return 'HRK';
                }
                return 'EUR';
            },
            CheckF2(vm, time)
            {
                if (vm.$store.state.user.f2_transition_small < time) {
                    return 2;
                }
                if (vm.$store.state.user.f2_transition_big < time) {
                    if (vm.$store.state.user.client.tax_system) {
                        return 2;
                    }
                    return 1;
                }
                return 0;
            },
            HandleServerError(vm, data = null) {
                vm.$LOCAL_BUS.TriggerEvent('popup', {
                    text: vm.$LANG.Get('alerts.serverError') + Date.now(),
                    type: 'alert'
                });
                StackTrace.get().then(stackframes => {
                    const stackString = stackframes.map(sf => sf.toString()).join('\n');
                    vm.$API.APICall('post', 'public/handle-server-error', { data: data, stack: stackString }, () => {});
                });
            },
            // Creates or updates an invoice and, for newly created invoices that carry a ZKI, fiscalizes them.
            // This is the single shared implementation of the "submit invoice then fiskal" sequence used by both
            // the invoice entity and the quick-invoice modal, so the fiscalization step is guaranteed identical.
            // Everything that legitimately differs between callers (validation, payload, success UX, error
            // presentation) stays in the caller through the hooks:
            //   method         'post' to create, 'put' to update
            //   id             the invoice id when updating, null when creating
            //   payload        the ready invoice payload
            //   onSuccess(invoice)      invoice created/updated (and fiscalized when applicable)
            //   onError(data)           the create/update call returned a non-200
            //   onFiskalError(data)     fiscalization returned a non-200 (non-blocking: onSuccess still runs)
            SubmitInvoice(vm, {method, id = null, payload, onSuccess = () => {}, onError = () => {}, onFiskalError = () => {}}) {
                let url = 'invoices' + (id ? '/' + id : '');
                vm.$API.APICall(method, url, payload, (data, success) => {
                    if (!success) {
                        onError(data);
                        return;
                    }
                    vm.$CROSS_TAB_BUS.TriggerEvent('reload-invoices', {});
                    let invoice = data.invoice;
                    // Only freshly created invoices are fiscalized, and only when they carry a ZKI.
                    if (method.toLowerCase() === 'post' && invoice.zki !== '') {
                        vm.$API.APICall('post', 'invoices/fiskal/' + invoice.id, {}, (fiskalData, fiskalSuccess) => {
                            if (!fiskalSuccess) {
                                onFiskalError(fiskalData);
                            }
                            onSuccess(invoice);
                        });
                    } else {
                        onSuccess(invoice);
                    }
                });
            }
        }

    }
}